import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styles/styled_components";
import { findIndex, map, propEq } from "ramda";

import TabButton, { ITabButton } from "components/shared/tab_button";
import TabPanel from "components/shared/tabpanel";
import useOnKey from "helpers/hooks/useOnKey";

interface ITablistProps {
  initialSelected?: string;
  label: string;
  onChange: (selectedId: string) => void;
  tabButtons: ITabButton[];
}

const StyledTablist = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius};
  background-color: ${({ theme }) => theme.colors.brandWhite};
  display: flex;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  overflow: hidden;
`;

enum KEYBOARD_DIRECTIONS {
  RIGHT = 1,
  LEFT = -1,
}

export default function Tablist({
  initialSelected,
  label,
  onChange,
  tabButtons,
}: ITablistProps) {
  const [selected, setSelected] = useState(initialSelected || tabButtons[0].id);
  const ref = useRef(null);
  // When selected changes, send up through onChange, if provided
  useEffect(
    () => {
      // Record changes to selected, only after we're mounted (check ref)
      if (ref.current) {
        onChange(selected);
      }
    },
    [selected]
  );

  // Accessibility hooks for keyboard events
  const moveOpt = useCallback(
    (direction: KEYBOARD_DIRECTIONS) => {
      setSelected(id => {
        const currentlySelectedIdx = findIndex(propEq("id", id), tabButtons);
        // Select the next index based on direction
        let nextIdx = currentlySelectedIdx + direction;
        if (nextIdx === tabButtons.length) {
          // Loop to beginning
          nextIdx = 0;
        } else if (nextIdx <= -1) {
          // Loop to end
          nextIdx = tabButtons.length - 1;
        }
        return tabButtons[nextIdx].id;
      });
    },
    [setSelected, tabButtons]
  );

  // Memoize these so they can be stably passed to useOnKey
  const moveRight = useCallback(() => moveOpt(KEYBOARD_DIRECTIONS.RIGHT), [
    moveOpt,
  ]);
  const moveLeft = useCallback(() => moveOpt(KEYBOARD_DIRECTIONS.LEFT), [
    moveOpt,
  ]);
  useOnKey(moveRight, "ArrowRight", ref);
  useOnKey(moveLeft, "ArrowLeft", ref);

  const buttons = map((opt: ITabButton) => {
    return (
      <TabButton
        key={opt.id}
        onClick={() => setSelected(opt.id)}
        selected={selected === opt.id}
        {...opt}
      />
    );
  }, tabButtons);

  return (
    <StyledTablist ref={ref} role="tablist" aria-label={label}>
      {buttons}
    </StyledTablist>
  );
}

export { ITablistProps, ITabButton, TabPanel };
